/**
 * 前端日志管理模块
 * 基于 loglevel 的日志系统封装
 *
 * 功能特性：
 * 1. 日志级别控制（TRACE, DEBUG, INFO, WARN, ERROR）
 * 2. 环境感知（开发环境详细日志，生产环境只记录错误）
 * 3. 日志前缀（时间戳、级别、模块名）
 * 4. 本地存储（可选）
 * 5. 远程发送（可选）
 */

import log from 'loglevel';
import prefix from 'loglevel-plugin-prefix';
import remote from 'loglevel-plugin-remote';

// 应用前缀插件
prefix.reg(log);

// 日志级别映射
const LOG_LEVELS = {
    TRACE: 'trace',
    DEBUG: 'debug',
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error',
    SILENT: 'silent'
};

/**
 * 日志管理器类
 */
class LogManager {
    constructor() {
        this.loggers = new Map();
        this.config = {
            enableConsole: true,
            enableLocalStorage: false,
            enableRemote: false,
            remoteUrl: '',
            maxLocalLogs: 1000,
            defaultLevel: LOG_LEVELS.INFO
        };

        // 初始化配置
        this.initConfig();
    }

    /**
     * 初始化配置
     */
    initConfig() {
        // 根据环境设置默认日志级别
        const env = process.env.NODE_ENV;
        const logLevel = process.env.VUE_APP_LOG_LEVEL;

        if (env === 'production') {
            // 生产环境：只记录警告和错误
            this.config.defaultLevel = LOG_LEVELS.WARN;
            this.config.enableLocalStorage = true;
        } else if (env === 'development') {
            // 开发环境：记录所有日志
            this.config.defaultLevel = LOG_LEVELS.DEBUG;
        } else {
            // 测试环境
            this.config.defaultLevel = LOG_LEVELS.INFO;
        }

        // 如果环境变量指定了日志级别，使用环境变量
        if (logLevel && LOG_LEVELS[logLevel.toUpperCase()]) {
            this.config.defaultLevel = LOG_LEVELS[logLevel.toUpperCase()];
        }

        // 配置前缀格式
        prefix.apply(log, {
            format(level, name, timestamp) {
                const time = new Date(timestamp).toLocaleTimeString('zh-CN');
                return `[${time}] [${level.toUpperCase()}]${name ? ` [${name}]` : ''}`;
            },
            timestampFormatter(date) {
                return date.toISOString();
            }
        });

        // 如果启用远程日志
        if (this.config.enableRemote && this.config.remoteUrl) {
            this.setupRemoteLogging();
        }
    }

    /**
     * 设置远程日志
     */
    setupRemoteLogging() {
        remote.apply(log, {
            url: this.config.remoteUrl,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            format: remote.json,
            // 批量发送
            interval: 5000, // 5秒发送一次
            backoff: {
                multiplier: 2,
                jitter: 0.1,
                limit: 30000
            }
        });
    }

    /**
     * 获取或创建日志器
     * @param {string} name - 日志器名称（通常是模块名）
     * @returns {Object} 日志器实例
     */
    getLogger(name = 'default') {
        if (!this.loggers.has(name)) {
            const logger = log.getLogger(name);

            // 设置日志级别
            logger.setLevel(this.config.defaultLevel);

            // 如果启用本地存储，包装日志方法
            if (this.config.enableLocalStorage) {
                this.wrapLoggerForLocalStorage(logger, name);
            }

            this.loggers.set(name, logger);
        }
        return this.loggers.get(name);
    }

    /**
     * 包装日志器以支持本地存储
     */
    wrapLoggerForLocalStorage(logger, name) {
        const originalMethods = {};
        const levels = ['trace', 'debug', 'info', 'warn', 'error'];

        levels.forEach((level) => {
            originalMethods[level] = logger[level].bind(logger);

            logger[level] = (...args) => {
                // 调用原始方法
                originalMethods[level](...args);

                // 保存到本地存储
                this.saveToLocalStorage({
                    timestamp: new Date().toISOString(),
                    level: level.toUpperCase(),
                    logger: name,
                    message: args
                        .map((arg) => {
                            if (typeof arg === 'object') {
                                try {
                                    return JSON.stringify(arg);
                                } catch (e) {
                                    return String(arg);
                                }
                            }
                            return String(arg);
                        })
                        .join(' ')
                });
            };
        });
    }

    /**
     * 保存日志到本地存储
     */
    saveToLocalStorage(logEntry) {
        try {
            const key = 'zcgc_logs';
            let logs = [];

            // 获取现有日志
            const stored = localStorage.getItem(key);
            if (stored) {
                try {
                    logs = JSON.parse(stored);
                } catch (e) {
                    logs = [];
                }
            }

            // 添加新日志
            logs.push(logEntry);

            // 限制数量
            if (logs.length > this.config.maxLocalLogs) {
                logs = logs.slice(-this.config.maxLocalLogs);
            }

            // 保存
            localStorage.setItem(key, JSON.stringify(logs));
        } catch (e) {
            // 忽略存储错误（可能是存储满了）
            console.warn('Failed to save log to localStorage:', e);
        }
    }

    /**
     * 获取本地存储的日志
     */
    getStoredLogs() {
        try {
            const stored = localStorage.getItem('zcgc_logs');
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            return [];
        }
    }

    /**
     * 清除本地存储的日志
     */
    clearStoredLogs() {
        localStorage.removeItem('zcgc_logs');
    }

    /**
     * 导出日志到文件
     */
    exportLogs() {
        const logs = this.getStoredLogs();
        const content = JSON.stringify(logs, null, 2);
        const blob = new Blob([content], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * 设置全局日志级别
     */
    setGlobalLevel(level) {
        const levelStr = typeof level === 'string' ? level : LOG_LEVELS[level];

        log.setLevel(levelStr);
        this.loggers.forEach((logger) => {
            logger.setLevel(levelStr);
        });

        this.config.defaultLevel = levelStr;
    }

    /**
     * 设置特定日志器的级别
     */
    setLoggerLevel(name, level) {
        const logger = this.getLogger(name);
        const levelStr = typeof level === 'string' ? level : LOG_LEVELS[level];
        logger.setLevel(levelStr);
    }
}

// 创建全局实例
const logManager = new LogManager();

// 导出默认日志器
const logger = logManager.getLogger();

// 便捷方法
export const trace = logger.trace.bind(logger);
export const debug = logger.debug.bind(logger);
export const info = logger.info.bind(logger);
export const warn = logger.warn.bind(logger);
export const error = logger.error.bind(logger);

// 导出管理器和工具
export { logManager, logger as default, LOG_LEVELS };

// 在开发环境下，将日志管理器挂载到 window 对象，方便调试
if (process.env.NODE_ENV === 'development') {
    window.__logManager = logManager;
    window.__logger = logger;

    // 提供全局快捷方法
    window.exportLogs = () => logManager.exportLogs();
    window.clearLogs = () => logManager.clearStoredLogs();
    window.showLogs = () => console.table(logManager.getStoredLogs());
}
